/**
 * Распаковка ZIP в браузере.
 *
 * Почему на клиенте, хотя doc-processor распаковывать умеет: архив с проектной
 * документацией — это сотни мегабайт, а PDF-сканы внутри почти не жмутся, так
 * что архив весит примерно как комплект. Одним запросом такое не проходит —
 * nginx рвёт на `client_max_body_size`, бэкенд держит тело в памяти в
 * нескольких копиях, а `MAX_UNCOMPRESSED_BYTES` в `parsers/archive.py` кладёт
 * потолок в 200 МБ. Распаковав здесь, получаем тот же плоский список файлов,
 * что и от выбора папки, и грузим его пачками в проверенных границах.
 *
 * Читаем по центральному каталогу, запись за записью (`zipReader`, как загрузка
 * РД архивом): раньше `fflate.unzip` держал в памяти вкладки весь архив и все
 * распакованные файлы сразу — на архиве в полгигабайта вкладка падала. Теперь
 * несжатая запись — срез исходного файла без копии, сжатая распаковывается
 * порциями в `Blob`. Плюс CRC-32 (битый файл не уходит молча), ZIP64 и имена
 * в cp866 (архивы «Проводника»).
 *
 * RAR так не распаковать — он уходит на бэкенд файлом, как раньше.
 */
import { matchesAccept } from './pickFiles';
import { listZip, readZipEntry } from './zipReader';

export const isZip = (file: File): boolean => file.name.toLowerCase().endsWith('.zip');

const NESTED_ARCHIVE = /\.(zip|rar|7z)$/i;
/** Сколько имён показывать в предупреждении, остальные — «и ещё N». */
const PREVIEW_NAMES = 5;

function isJunkPath(path: string): boolean {
  const name = path.split('/').pop() ?? '';
  return (
    path.startsWith('__MACOSX/') || // служебная ветка архивов, собранных на macOS
    name === '.DS_Store' ||
    name === 'Thumbs.db' ||
    name.startsWith('~$')
  );
}

function names(list: string[]): string {
  const shown = list
    .slice(0, PREVIEW_NAMES)
    .map((n) => `«${n}»`)
    .join(', ');
  return list.length > PREVIEW_NAMES ? `${shown} и ещё ${list.length - PREVIEW_NAMES}` : shown;
}

/** Что из архива не вошло в комплект и почему. */
interface Skipped {
  encrypted: string[];
  unsupported: string[];
  nested: string[];
  broken: string[];
}

/**
 * Файлы из архива, готовые к отправке, и то, что прочитать не удалось.
 *
 * Имя файла — путь внутри архива целиком, а не basename: в комплекте ПД
 * подпапки разделов сплошь содержат одноимённые «Титул.pdf» / «Содержание.pdf»,
 * и по basename они схлопнулись бы дедупом в один файл.
 */
async function readArchive(
  archive: File,
  accept: string,
): Promise<{ files: File[]; skipped: Skipped }> {
  const files: File[] = [];
  const skipped: Skipped = { encrypted: [], unsupported: [], nested: [], broken: [] };
  for (const entry of await listZip(archive)) {
    if (entry.size === 0 || isJunkPath(entry.path)) continue;
    if (!matchesAccept(entry.path, accept)) {
      // Вложенный архив там, где архивы не принимают, — сказать, а не терять
      // молча; где принимают (комплект договора) — уходит файлом, бэкенд распакует.
      if (NESTED_ARCHIVE.test(entry.path)) skipped.nested.push(entry.path);
      continue;
    }
    if (entry.encrypted) {
      skipped.encrypted.push(entry.path);
      continue;
    }
    if (entry.method !== 0 && entry.method !== 8) {
      skipped.unsupported.push(entry.path);
      continue;
    }
    try {
      files.push(new File([await readZipEntry(archive, entry)], entry.path));
    } catch {
      skipped.broken.push(entry.path);
    }
  }
  return { files, skipped };
}

/** Файлы из архива (без предупреждений о пропущенном). */
export async function filesFromZip(archive: File, accept: string): Promise<File[]> {
  return (await readArchive(archive, accept)).files;
}

/**
 * Разворачивает архивы в выбранном списке, остальное пропускает как есть.
 * Возвращает и предупреждения — юристу важно понимать, почему из архива
 * приехало не то, что он ожидал.
 */
export async function expandArchives(
  files: File[],
  accept: string,
): Promise<{ files: File[]; warnings: string[] }> {
  const out: File[] = [];
  const warnings: string[] = [];

  for (const file of files) {
    if (!isZip(file)) {
      out.push(file);
      continue;
    }
    let read: Awaited<ReturnType<typeof readArchive>>;
    try {
      read = await readArchive(file, accept);
    } catch {
      // Битый или незнакомый архив — не роняем всю форму: отдаём файл на
      // бэкенд, вдруг он справится.
      warnings.push(`Не удалось распаковать «${file.name}» — отправим архив как есть.`);
      out.push(file);
      continue;
    }
    const { encrypted, unsupported, nested, broken } = read.skipped;
    if (encrypted.length > 0) {
      warnings.push(
        `«${file.name}»: зашифрованы — пропущены (${encrypted.length}): ${names(encrypted)}. ` +
          'Упакуйте без пароля.',
      );
    }
    if (unsupported.length > 0) {
      warnings.push(
        `«${file.name}»: сжаты неподдерживаемым методом — пропущены (${unsupported.length}): ` +
          `${names(unsupported)}. Перепакуйте в обычный ZIP или выберите папку.`,
      );
    }
    if (nested.length > 0) {
      warnings.push(
        `«${file.name}»: вложенный архив — распакуйте и выберите папкой (${nested.length}): ` +
          `${names(nested)}.`,
      );
    }
    if (broken.length > 0) {
      warnings.push(
        `«${file.name}»: файлы повреждены — пропущены (${broken.length}): ${names(broken)}.`,
      );
    }
    if (read.files.length === 0) {
      warnings.push(`В архиве «${file.name}» не нашлось документов подходящего формата.`);
      continue;
    }
    out.push(...read.files);
  }
  return { files: out, warnings };
}
